/**
 * Dynamic per-week activity columns for the journey segments (Qualified
 * Prospects / Cool Lead). Mirrors the legacy `customer-journey-list-config`
 * week generator: for the last 4 ISO weeks (Mon–Sun, ending with the current
 * week) it renders each customer's journey notes bucketed into the week their
 * `customer_journey_note_updated_at` falls in — grouped by step, shown as a
 * tone-coded Chip + note text, or an italic muted "No activity" when empty.
 *
 * Weeks are derived from LOCAL dates (getFullYear/getMonth/getDate) — never
 * `toISOString()` — so week boundaries respect the viewer's timezone.
 */
import type { ReactNode } from 'react';
import type { ColumnDef } from '@vnetwork/ui/data';
import { Chip, type ChipTone } from '@vnetwork/ui/primitives';
import { formatDayMonth } from '@/lib/format';
import type { CustomerListRow } from '@/api/customers';

type Col = ColumnDef<CustomerListRow, unknown>;

/** Number of trailing ISO weeks to render (oldest → newest). */
const WEEK_COUNT = 4;

/** Marker the journey editor prepends to flag an important note. */
const IMPORTANT_NOTE_MARKER = '[[DC_IMPORTANT_NOTE_V1]] ';

/* ── Types (loose views over the untyped journey payload) ─────────────────── */

interface JourneyNote {
  customer_journey_note?: string;
  customer_journey_note_step?: string;
  customer_journey_note_updated_at?: string;
}
interface JourneyEntry {
  customer_journey_notes?: JourneyNote[];
}

export interface WeekRange {
  /** 1 = oldest … WEEK_COUNT = current week. */
  weekNumber: number;
  start: Date;
  end: Date;
  /** `W{n}: DD/MM – DD/MM`. */
  label: string;
}

/* ── Step → EN label + Chip tone ──────────────────────────────────────────── */

const STEP_META: Record<string, { label: string; tone: ChipTone }> = {
  searching: { label: 'Contact & Research', tone: 'info' },
  presentation: { label: 'Meeting', tone: 'info' },
  poc: { label: 'POC', tone: 'primary' },
  contract: { label: 'Deal & Contract', tone: 'warning' },
  close_win: { label: 'Closed Won', tone: 'success' },
  close_lose: { label: 'Closed Lost', tone: 'danger' },
  terminate: { label: 'Terminated', tone: 'neutral' },
};

/** Humanize an unknown step slug: `some_step` → `Some step`. */
function humanizeStep(step: string): string {
  const spaced = step.replace(/_/g, ' ').trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : step;
}

function stepMeta(step: string): { label: string; tone: ChipTone } {
  return STEP_META[step] ?? { label: humanizeStep(step), tone: 'neutral' };
}

/* ── Week range generation (LOCAL dates) ──────────────────────────────────── */

/** Local midnight of `d` (mutation-free). */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday 00:00 of the ISO week containing `d` (local time). */
function startOfIsoWeek(d: Date): Date {
  const day = startOfDay(d);
  // JS: Sun=0..Sat=6 → ISO Mon-offset: Sun→6, else weekday-1.
  const offset = (day.getDay() + 6) % 7;
  day.setDate(day.getDate() - offset);
  return day;
}

/** Sunday 23:59:59.999 of the ISO week starting at Monday `weekStart`. */
function endOfIsoWeek(weekStart: Date): Date {
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * The last {@link WEEK_COUNT} ISO weeks (Mon–Sun) ending with the week that
 * contains `now`. Returned oldest-first so `weekNumber` 1 → oldest, matching
 * the legacy `W1 … W4` header order (W4 = current week).
 */
export function weekRanges(now: Date = new Date()): WeekRange[] {
  const currentWeekStart = startOfIsoWeek(now);
  const ranges: WeekRange[] = [];
  for (let i = WEEK_COUNT - 1; i >= 0; i--) {
    const start = new Date(currentWeekStart);
    start.setDate(start.getDate() - i * 7);
    const end = endOfIsoWeek(start);
    const weekNumber = WEEK_COUNT - i;
    ranges.push({
      weekNumber,
      start,
      end,
      label: `W${weekNumber}: ${formatDayMonth(start.toString())} – ${formatDayMonth(end.toString())}`,
    });
  }
  return ranges;
}

/* ── Note bucketing ───────────────────────────────────────────────────────── */

/** System-generated notes are step-transition breadcrumbs — excluded like legacy. */
const SYSTEM_NOTE_PATTERNS: RegExp[] = [
  /^Chuyển sang bước/i,
  /^Chuyển từ .+ sang/i,
  /^Moved to step/i,
  /^Moved from .+ to/i,
  /^Step changed to/i,
  /^Hoàn thành quá trình/i,
  /^Journey completed/i,
  /^Mở lại từ/i,
  /^Re-engaged from/i,
  /^Đóng deal/i,
  /^Deal closed/i,
  /^Tạo POC/i,
  /^POC created/i,
  /^Tạo hợp đồng/i,
  /^Contract created/i,
  /^step-changed-to/i,
];

function isSystemNote(text: string): boolean {
  const t = text.trim();
  return !!t && SYSTEM_NOTE_PATTERNS.some((re) => re.test(t));
}

/** Strip the important marker; report whether it was present. */
function parseNote(raw: string): { text: string; isImportant: boolean } {
  if (raw.startsWith(IMPORTANT_NOTE_MARKER)) {
    return { text: raw.slice(IMPORTANT_NOTE_MARKER.length), isImportant: true };
  }
  return { text: raw, isImportant: false };
}

function journeyEntries(row: CustomerListRow): JourneyEntry[] {
  const list = (row as { customer_journey?: unknown }).customer_journey;
  return Array.isArray(list) ? (list as JourneyEntry[]) : [];
}

/** All non-system notes of a row whose updated_at falls inside `[start, end]`. */
function notesInWeek(row: CustomerListRow, week: WeekRange): { step: string; text: string; isImportant: boolean }[] {
  const out: { step: string; text: string; isImportant: boolean }[] = [];
  const startMs = week.start.getTime();
  const endMs = week.end.getTime();
  for (const entry of journeyEntries(row)) {
    for (const note of entry.customer_journey_notes ?? []) {
      const iso = note.customer_journey_note_updated_at;
      if (!iso) continue;
      const t = new Date(iso).getTime();
      if (isNaN(t) || t < startMs || t > endMs) continue;
      const parsed = parseNote(note.customer_journey_note ?? '');
      if (!parsed.text || isSystemNote(parsed.text)) continue;
      out.push({ step: note.customer_journey_note_step || 'unknown', ...parsed });
    }
  }
  return out;
}

/* ── Cell renderer ────────────────────────────────────────────────────────── */

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

function WeekActivityCell({ row, week }: { row: CustomerListRow; week: WeekRange }): ReactNode {
  const notes = notesInWeek(row, week);
  if (notes.length === 0) return <span style={muted}>No activity</span>;

  // Group by step, preserving first-seen order.
  const groups = new Map<string, { step: string; text: string; isImportant: boolean }[]>();
  for (const n of notes) {
    const g = groups.get(n.step);
    if (g) g.push(n);
    else groups.set(n.step, [n]);
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--vn-space-1)',
        maxHeight: 120,
        overflowY: 'auto',
        paddingBlock: 'var(--vn-space-1)',
      }}
    >
      {Array.from(groups.entries()).map(([step, items]) => {
        const { label, tone } = stepMeta(step);
        const important = items.some((i) => i.isImportant);
        const combined = items.map((i) => i.text).join('; ');
        const display = items.length > 1 ? `${items[0].text} (+${items.length - 1})` : items[0].text;
        return (
          <div
            key={step}
            title={combined}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--vn-space-1)', minWidth: 0 }}
          >
            <Chip tone={tone} appearance="subtle" size="sm" style={{ flexShrink: 0 }}>
              {label}
            </Chip>
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontSize: 'var(--vn-font-size-xs)',
                color: important ? 'var(--vn-danger-text)' : 'var(--vn-fg-muted)',
                fontWeight: important ? 'var(--vn-font-weight-medium)' : undefined,
              }}
            >
              {display}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Column factory ───────────────────────────────────────────────────────── */

/** Stable column ids for the week columns (for Display-options registration). */
export const JOURNEY_WEEK_COLUMN_IDS = Array.from({ length: WEEK_COUNT }, (_, i) => `journey_week_${i + 1}`);

/**
 * Build the {@link WEEK_COUNT} dynamic week columns for the journey segments.
 * Pure — pass `now` in tests for deterministic ranges.
 */
export function journeyWeekColumns(now: Date = new Date()): Col[] {
  return weekRanges(now).map((week) => ({
    id: `journey_week_${week.weekNumber}`,
    header: week.label,
    minSize: 240,
    cell: ({ row }) => <WeekActivityCell row={row.original} week={week} />,
  }));
}
